import { ChangeDetectionStrategy, Component, inject, OnDestroy, Signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormUtils } from '@rx-applications/shared/utils-forms';
import { addAbsolutePathPrefix } from '@rx-applications/shared/utils';
import {
  AuthConfig,
  AuthConfigService,
  AuthFacade,
  AuthProvider,
  AuthRoutes,
} from '@rx-applications/shared/auth/data-access';
import { TRANSLATION_SCOPE, translationProvider } from './i18n/translation.provider';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { RxAlertModule, RxButtonModule, RxInputModule } from '@roomex/rx-components';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'rx-forgot-password',
  templateUrl: './forgot-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TranslocoModule, RxButtonModule, RxAlertModule, ReactiveFormsModule, RxInputModule, RouterLink],
  providers: [translationProvider],
})
export class ForgotPasswordComponent extends FormUtils implements OnDestroy {
  protected readonly TRANSLATION_SCOPE = TRANSLATION_SCOPE;
  protected readonly addAbsolutePathPrefix = addAbsolutePathPrefix;
  protected readonly authConfig: AuthConfig = inject(AuthConfigService).config;
  protected readonly AuthProvider = AuthProvider;
  authRoutes = AuthRoutes;

  showHelperText: Signal<boolean> = toSignal(
    inject(ActivatedRoute).data.pipe(
      takeUntilDestroyed(),
      map((data) => !!data['showHelperText'])
    ),
    {
      initialValue: false,
    }
  );

  form: UntypedFormGroup;

  constructor(public authFacade: AuthFacade, private fb: UntypedFormBuilder) {
    super();
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnDestroy() {
    // When a user reset password and then, go to another route and come back
    // to the forgot password page, it shows the success notification. The
    // below action reset the state when component is destroyed.
    this.authFacade.hideSuccessMessage();
    this.authFacade.hideErrorMessage();
  }

  onSubmit() {
    if (this.form.invalid) {
      this.markAllAsTouched(this.form);
      return;
    }

    this.authFacade.requestPassword(this.form.value.email);
  }
}
